import { Constants } from "@constants";
import { createSlice } from "@reduxjs/toolkit";
import apiService from "@utils/apiService";

const initialState = {
  currentPage: 0,
  loading: false,
  error: false,
  data: [],
  loadingMore: false,
  errorMore: false,
  filters: {
    industries: [],
    businessFunctions: [],
    valueIndicators: [],
  },
  selectedFilters: {
    industries: [],
    businessFunctions: [],
    valueIndicators: [],
  },
  errorOnFilter: false,
  searchToken: '',
  selectedChip: Constants.FILTER_CHIP.ALL_KEY,
  searchInfo: { token: '', type: '' }
};

function getPayload(payload) {
  return {
    token: payload?.searchInfo?.token || '',
    industries: payload?.selectedFilters?.industries || [],
    businessFunctions: payload?.selectedFilters?.businessFunctions || [],
    valueIndicators: payload?.selectedFilters?.valueIndicators || [],
    tab: payload?.tab || Constants.FILTER_CHIP.ALL_KEY,
    pageNum: payload?.pageNum || 0,
    pageSize: payload?.pageSize || 20,
  }
}

function getFiltersPayload(filters = {}) {
  const { FIELDS, LABELS } = Constants.CASE_EXAMPLES.FILTERS;
  const filtersPayload = []
  FIELDS.map((key) => {
    if (filters[key]?.length) {
      filtersPayload.push(({
        label: LABELS[key],
        filterValues: filters[key],
      }))
    }
  });
  return filtersPayload;
}

export const caseExamplesSlice = createSlice({
  name: "caseExamples",
  initialState,
  reducers: {
    setCurrentPage: (state, action) => ({
      ...state,
      currentPage: action.payload,
    }),
    caseExamplesStart: (state) => ({
      ...state,
      loading: true,
      error: false,
      errorOnFilter: false,
    }),
    caseExamplesSuccess: (state, action) => ({
      ...state,
      loading: false,
      error: false,
      data: action.payload
    }),
    caseExamplesFailure: (state) => ({
      ...state,
      loading: false,
      error: true,
    }),
    caseExamplesLoadMoreStart: (state, action) => ({
      ...state,
      loadingMore: true,
      currentPage: action.payload,
      errorMore: false,
    }),
    caseExamplesLoadMoreSuccess: (state, action) => ({
      ...state,
      loadingMore: false,
      data: {
        ...state.data,
        caseStudyList: [
          ...state.data.caseStudyList,
          ...(action.payload?.caseStudyList || [])
        ]
      },
      errorMore: false,
    }),
    caseExamplesLoadMoreFailure: (state) => ({
      ...state,
      loadingMore: false,
      errorMore: true,
    }),
    updateFilters: (state, action) => ({
      ...state,
      errorOnFilter: false,
      filters: {
        industries: action.payload.industry.map(item => item.value && item),
        businessFunctions: action.payload.businessFunction.map(item => item.value && item),
        valueIndicators: action.payload.valueIndicator.map(item => item.value && item),
      }
    }),
    updateFiltersFailure: (state) => ({
      ...state,
      errorOnFilter: true,
    }),
    updateSelectedFilters: (state, action) => ({
      ...state,
      selectedFilters: action.payload, 
    }),
    updateCaseExamples: (state, action) => ({
      ...state,
      data: action.payload,
    }),
    updateSearchToken: (state, action) => ({
      ...state,
      searchToken: action.payload
    }),
    updateSelectedChip: (state, action) => ({
      ...state,
      selectedChip: action.payload,
    }),
    updateSearchInfo: (state, action) => ({
      ...state,
      searchInfo: action.payload,
    }),
    resetCaseExamples: () => ({
      ...initialState,
    })
},
});

export const { 
  setCurrentPage,
  caseExamplesStart,
  caseExamplesFailure,
  caseExamplesSuccess,
  caseExamplesLoadMoreStart,
  caseExamplesLoadMoreSuccess,
  caseExamplesLoadMoreFailure,
  updateFilters,
  updateSelectedFilters,
  updateFiltersFailure,
  updateCaseExamples,
  updateSearchToken,
  updateSelectedChip,
  updateSearchInfo,
  resetCaseExamples,
} = caseExamplesSlice.actions;

export const getCaseExamples = (payload) => async (dispatch) => {
  dispatch(caseExamplesStart())
  dispatch(setCurrentPage(0));
  dispatch(updateFilterData(payload));
  const apiPath =  `${Constants.API.CASE_EXAMPLES_SEMANTIC_SEARCH}${payload?.searchInfo?.searchType || Constants.SEARCH_TYPE.ALL}`;
  apiService.post(apiPath, getPayload(payload))
    .then((response) => {
      if (!Object.keys(response.data?.data).length) {
        dispatch(caseExamplesFailure());
        return;
      }
      dispatch(caseExamplesSuccess(response.data?.data))
    })
    .catch(() => {
      dispatch(caseExamplesFailure());
    })
}

export const loadMoreCaseExamples = (payload) => async (dispatch) => {
  dispatch(caseExamplesLoadMoreStart(payload.pageNum));
  const apiPath =  `${Constants.API.CASE_EXAMPLES_SEMANTIC_SEARCH}${payload?.searchInfo?.searchType || Constants.SEARCH_TYPE.ALL}`;
  apiService.post(apiPath, getPayload(payload))
    .then((response) => {
      dispatch(caseExamplesLoadMoreSuccess(response.data?.data))
    })
    .catch(() => {
      dispatch(caseExamplesLoadMoreFailure());
    })
}

export const updateFilterData = (payload) => async (dispatch) => {
  const apiPath = `${Constants.API.CASE_EXAMPLES_SEMANTIC_FILTER}${payload?.searchInfo?.searchType || Constants.SEARCH_TYPE.ALL}`;
  apiService.post(apiPath, getFiltersPayload(payload?.selectedFilters), {
    params: {
      token: payload?.searchInfo?.token,
      tab: payload?.tab
    }
  }).then((response) => {
    dispatch(updateFilters(response.data?.data));
    if (payload?.selectedFilters && Object.keys(payload?.selectedFilters)?.length) {
      dispatch(updateSelectedFilters(payload?.selectedFilters))
    }
  }).catch(() => {
    dispatch(updateFiltersFailure());
  })
}

export default caseExamplesSlice.reducer;
